import CloseRounded from "@mui/icons-material/CloseRounded";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useId } from "react";
import { Link as RouterLink } from "react-router-dom";
import { PlayerProfileView } from "../../modules/performance/components/PlayerProfileView";
import { playerHandleSuffix } from "@bw/shared/components";

/**
 * A lightweight profile peek that reuses the routed `PlayerProfileView` body so
 * a player can be inspected without leaving the current flow, with a link to the
 * standalone page. Lives in app/ so multiple journeys can consume it without a
 * sibling-module import.
 *
 * Native MUI Dialog owns the backdrop, focus trap, focus restoration, and escape
 * behavior. Escape propagation is stopped so a parent modal is not also closed.
 */
export function PlayerPeekDialog({
  userId,
  displayName,
  onClose,
}: {
  userId: string;
  displayName?: string | null;
  onClose: () => void;
}) {
  const titleId = useId();

  return (
    <Dialog
      open
      aria-labelledby={titleId}
      maxWidth="md"
      onClose={onClose}
      onKeyDown={(event) => event.stopPropagation()}
      slotProps={{
        backdrop: {
          "data-testid": "player-peek-backdrop",
        } as Record<string, string>,
        paper: { sx: { maxHeight: "min(90dvh, 48rem)" } },
      }}
    >
      <DialogTitle id={titleId} component="div">
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography component="h2" variant="h3">
            {displayName || "Player"}{" "}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              {playerHandleSuffix(userId)}
            </Typography>
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" title="Close">
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <PlayerProfileView userId={userId} />
      </DialogContent>

      <DialogActions>
        <Button
          component={RouterLink}
          to={`/players/${userId}`}
          onClick={onClose}
        >
          View full profile
        </Button>
      </DialogActions>
    </Dialog>
  );
}
